import {
  getElementStats,
  getElementStatsByIdentifier,
} from "core-integration/src/store/element-stats/reducers";
import { IElementStat } from "core-integration/src/store/element-stats/types";
import { getElementTypesBySquadPosition } from "core-integration/src/store/element-types/reducers";
import { getFutureEvents } from "core-integration/src/store/events/reducers";
import {
  listTableBody,
  listTableElementColumn,
  listTableHeader,
  listTablePosition,
  listTablePositionCell,
} from "plos/src/styles/listTables.css";
import { fixedTable, scrollContainer } from "plos/src/styles/utils.css";
import { getStartingAndBenchPlayers } from "plos/src/utils/getStartingAndBenchPlayers";
import { positionsByElementType } from "plos/src/utils/positionsByElementType";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useOverlayContext } from "../../../contexts/OverlayContext";
import {
  fdrEventCount,
  useOverlayColumns,
} from "../../../hooks/useOverlayColumns";
import { OverlayHeaderCells } from "../../ListViewTable/OverlayCells";
import EntryEventTableRow from "./EntryEventTableRow/EntryEventTableRow";
import { EntryEventTableProps } from "./types";

const EntryEventTable = ({
  dataById,
  elementsById,
  picks,
  chipName,
  renderDreamTeam,
}: EntryEventTableProps) => {
  const { t } = useTranslation("points");

  const { selectedOverlay } = useOverlayContext();
  const stats = useSelector(getElementStats);
  const statsByName: Record<string, IElementStat> = useSelector(
    getElementStatsByIdentifier,
  );
  const fdrEvents = useSelector(getFutureEvents).slice(0, fdrEventCount);
  const elementTypesByPosition = useSelector(getElementTypesBySquadPosition);

  const { benchPlayers, playersByElementType } = getStartingAndBenchPlayers(
    picks,
    elementsById,
    chipName,
  );

  const columns = useOverlayColumns(selectedOverlay, {
    stats,
    statsByName,
    fdrEvents,
  });

  // Table structure: Player column + the overlay-driven data columns
  const totalColumns = 1 + columns.length;

  return (
    <div className={scrollContainer}>
      <table
        aria-label={t("pointsList", "Points List")!}
        className={fixedTable}
      >
        <thead>
          <tr>
            <th className={listTableElementColumn} scope="col">
              <div className={listTableHeader}>{t("player", "Player")}</div>
            </th>
            <OverlayHeaderCells columns={columns} />
          </tr>
        </thead>

        {Object.keys(positionsByElementType).map((et) => {
          const positions = positionsByElementType[et];
          const positionPicks = playersByElementType[et];

          // Skip if no starting players in this position
          if (positionPicks.length === 0) {
            return null;
          }

          return (
            <tbody className={listTableBody} key={et}>
              <tr>
                <th
                  scope="rowgroup"
                  className={listTablePositionCell}
                  colSpan={totalColumns}
                >
                  <div className={listTablePosition}>
                    {positionPicks.length > 1
                      ? elementTypesByPosition[positions[0]].thisType
                          .plural_name
                      : elementTypesByPosition[positions[0]].thisType
                          .singular_name}
                  </div>
                </th>
              </tr>
              {positionPicks.map((p) => (
                <EntryEventTableRow
                  key={p.element}
                  pick={p}
                  element={elementsById[p.element]}
                  overlay={selectedOverlay}
                  stats={stats}
                  data={
                    dataById
                      ? dataById[p.element].stats
                      : elementsById[p.element]
                  }
                  chipName={chipName}
                  renderDreamTeam={renderDreamTeam}
                />
              ))}
            </tbody>
          );
        })}

        {benchPlayers.length > 0 && (
          <tbody className={listTableBody}>
            <tr>
              <th
                scope="rowgroup"
                className={listTablePositionCell}
                colSpan={totalColumns}
              >
                <div className={listTablePosition}>
                  {t("substitutes", "Substitutes")}
                </div>
              </th>
            </tr>
            {benchPlayers.map((p) => (
              <EntryEventTableRow
                key={p.element}
                pick={p}
                element={elementsById[p.element]}
                overlay={selectedOverlay}
                stats={stats}
                data={
                  dataById ? dataById[p.element].stats : elementsById[p.element]
                }
                chipName={chipName}
                renderDreamTeam={renderDreamTeam}
              />
            ))}
          </tbody>
        )}
      </table>
    </div>
  );
};

export default EntryEventTable;
